/**
 * Parse `/completed/:id` the same way Match details do: thin scorecards often omit inns*
 * while the event scoreboard still has runs.N / wkts.N and the who-won line.
 */
import { unwrapCricketScorecard } from "../../../utils/cricketInnsTeamScores";
import {
    commentIndicatesCricketMatchResult,
    isCricketRrrComment,
    isDecimalTeamWinner,
    resultCommentFromEventStatus,
} from "../../Trader/Cricket/decimalEventStatus";

export type LeagueCompletedScoreboardRow = { name?: string; value?: unknown };

export type LeagueCompletedArchive = {
    scorecard: Record<string, unknown>;
    scoreboard: LeagueCompletedScoreboardRow[];
    /** Who-won line (never raw chase / toss commentary). Empty when unavailable. */
    matchComment: string;
};

function scoreboardValue(scoreboard: LeagueCompletedScoreboardRow[] | undefined, name: string): string {
    if (!Array.isArray(scoreboard)) return "";
    const row = scoreboard.find((item) => item?.name === name);
    return row?.value != null ? String(row.value).trim() : "";
}

function scoreboardInt(scoreboard: LeagueCompletedScoreboardRow[] | undefined, name: string): number | null {
    const v = parseInt(scoreboardValue(scoreboard, name), 10);
    return Number.isNaN(v) ? null : v;
}

function scorecardHasInns(scorecard: Record<string, unknown> | null): boolean {
    return Boolean(scorecard && (scorecard.inns1 != null || scorecard.inns2 != null));
}

/** Build inns1–inns4 from scoreboard runs.N / wkts.N (Match list / Match details fallback). */
export function innsFromCompletedScoreboard(
    scoreboard: LeagueCompletedScoreboardRow[] | undefined,
): Record<string, { runs: number; wkts: number }> {
    const inns: Record<string, { runs: number; wkts: number }> = {};
    for (let i = 1; i <= 4; i += 1) {
        const runs = scoreboardInt(scoreboard, `runs.${i}`);
        const wkts = scoreboardInt(scoreboard, `wkts.${i}`);
        if (runs == null && wkts == null) continue;
        inns[`inns${i}`] = { runs: runs ?? 0, wkts: wkts ?? 0 };
    }
    return inns;
}

/**
 * Prefer scorecard inns; when the archive only kept Event scoreboard totals, fill inns*
 * so the league score bars can render (same as fixturelist/completed list cards).
 */
export function enrichCompletedScorecard(
    scorecard: unknown,
    scoreboard: LeagueCompletedScoreboardRow[] | undefined,
): Record<string, unknown> | null {
    const base = unwrapCricketScorecard(scorecard);
    const fromSb = innsFromCompletedScoreboard(scoreboard);
    if (scorecardHasInns(base)) {
        const sc = { ...(base as Record<string, unknown>) };
        if (!String(sc.bat_now ?? sc.batNow ?? "").trim()) {
            const bat =
                scoreboardValue(scoreboard, "Current Batting Team") ||
                scoreboardValue(scoreboard, "bat_now");
            if (bat) sc.bat_now = bat;
        }
        return sc;
    }
    if (Object.keys(fromSb).length === 0) {
        return base ? { ...(base as Record<string, unknown>) } : null;
    }
    const bat =
        scoreboardValue(scoreboard, "Current Batting Team") ||
        scoreboardValue(scoreboard, "bat_now") ||
        scoreboardValue(scoreboard, "batting.2");
    return {
        ...(base ? (base as Record<string, unknown>) : {}),
        ...fromSb,
        ...(bat ? { bat_now: bat } : {}),
    };
}

/**
 * Who-won line — same priority as DecimalSocketContext EventProc / backend
 * `deriveFinishedMatchResultComment` used by Match details and finished match cards.
 */
export function deriveCompletedMatchComment(
    scoreboard: LeagueCompletedScoreboardRow[] | undefined,
    ...statusCandidates: unknown[]
): string {
    let result = "";
    for (const status of statusCandidates) {
        const fromStatus = resultCommentFromEventStatus(status);
        if (fromStatus) {
            result = fromStatus;
            break;
        }
    }

    const get = (name: string) => scoreboardValue(scoreboard, name);
    const winnerName = get("winner");
    const winnerOk = isDecimalTeamWinner(winnerName);

    if (!result) {
        const marginKeys = [
            "wonby",
            "won_by",
            "win_by",
            "winning_margin",
            "result",
            "match_result",
            "victory_margin",
        ];
        for (const key of marginKeys) {
            const val = get(key);
            if (!val) continue;
            if (/\bwon\b|\bwins?\b|\bruns?\b|\bwickets?\b|\btied?\b|\bdraw/i.test(val)) {
                result = val;
                break;
            }
            if (winnerOk && /\bruns?\b|\bwickets?\b/i.test(val)) {
                result = `${winnerName} won by ${val}`;
                break;
            }
        }
    }

    if (!result) {
        const bat1 = get("batting.1") || get("home");
        const bat2 = get("batting.2") || get("away");
        const r1 = scoreboardInt(scoreboard, "runs.1");
        const r2 = scoreboardInt(scoreboard, "runs.2");
        const w2 = scoreboardInt(scoreboard, "wkts.2");
        if (r1 != null && r2 != null) {
            if (r2 > r1) {
                const name = bat2 || (winnerOk ? winnerName : "");
                if (name) {
                    const wicketsInHand = w2 == null ? 10 : Math.max(0, 10 - w2);
                    result = `${name} won by ${wicketsInHand} wicket${wicketsInHand !== 1 ? "s" : ""}`;
                }
            } else if (r1 > r2) {
                const chaseNotStarted = r2 === 0 && (w2 == null || w2 === 0);
                if (!chaseNotStarted || winnerOk) {
                    const name = bat1 || (winnerOk ? winnerName : "");
                    if (name) result = `${name} won by ${r1 - r2} runs`;
                }
            } else if (!(r1 === 0 && r2 === 0 && !winnerOk)) {
                result = "Match tied";
            }
        }
    }

    if (!result) {
        let comment = get("match_comment");
        if (isCricketRrrComment(comment)) comment = "";
        if (commentIndicatesCricketMatchResult(comment)) {
            result = comment;
        } else if (winnerOk) {
            result = `${winnerName} wins.`;
        }
    }

    return result;
}

/** Pull scorecard + scoreboard + who-won line from a `/completed/:id` JSON body. */
export function parseCompletedCricketArchive(payload: unknown): LeagueCompletedArchive | null {
    const data =
        payload && typeof payload === "object" && "data" in (payload as object)
            ? (payload as { data?: unknown }).data
            : payload;
    if (data == null || Array.isArray(data) || typeof data !== "object") return null;

    const root = data as {
        scorecard?: unknown;
        event?: unknown;
    };

    const sc0 = Array.isArray(root.scorecard) ? root.scorecard[0] : root.scorecard;
    const fromScorecard =
        sc0 && typeof sc0 === "object" && !Array.isArray(sc0)
            ? ((sc0 as { msg_data?: unknown }).msg_data ?? sc0)
            : null;

    const ev0 = Array.isArray(root.event) ? root.event[0] : root.event;
    const eventMd =
        ev0 && typeof ev0 === "object" && !Array.isArray(ev0)
            ? (((ev0 as { msg_data?: unknown }).msg_data ?? ev0) as Record<string, unknown>)
            : null;

    const fromEventScorecard = eventMd?.scorecard;
    const rawScorecard =
        fromScorecard && typeof fromScorecard === "object" && !Array.isArray(fromScorecard)
            ? fromScorecard
            : fromEventScorecard && typeof fromEventScorecard === "object" && !Array.isArray(fromEventScorecard)
              ? fromEventScorecard
              : null;

    const scoreboard = Array.isArray(eventMd?.scoreboard)
        ? (eventMd.scoreboard as LeagueCompletedScoreboardRow[])
        : [];

    const scorecard = enrichCompletedScorecard(rawScorecard, scoreboard);
    if (!scorecard) return null;

    const eventStatus =
        (rawScorecard as { event_status?: unknown } | null)?.event_status ??
        eventMd?.event_status ??
        "";
    const gamePhase = scoreboardValue(scoreboard, "game_phase");
    const matchComment = deriveCompletedMatchComment(scoreboard, gamePhase, eventStatus);

    if (matchComment && !String(scorecard.result ?? "").trim()) {
        scorecard.result = matchComment;
    }

    return { scorecard, scoreboard, matchComment };
}
